import React, { useEffect, useState } from 'react';
import { useNavigateLegacy as useNavigate } from '@leagueplatform/routing';
import {
  Box,
  HeadingText,
  GDSStyleObject,
  Card,
  ParagraphText,
  TextAction,
  StackLayout,
  StatusBadge,
} from '@leagueplatform/genesis-core';
import { useStaticAsset } from '@leagueplatform/asset-config';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import { getAccessToken, formatDate } from '../helper/helpers';
import * as CONSTANTS from '../utils/constant';
import { newsletter } from '../newsletter.config';
import { ErrorModal } from './errorModal';
import { NEWSLETTER_ASSET_KEYS } from '../types/newsletter.types';

export function NewsLetter() {
  const navigate = useNavigate();
  const leftArrow = useStaticAsset(NEWSLETTER_ASSET_KEYS.LEFT_ARROW);
  const rightChevGray = useStaticAsset(NEWSLETTER_ASSET_KEYS.RIGHT_CHEV_GRAY);
  const bannerImage = useStaticAsset(NEWSLETTER_ASSET_KEYS.BANNER_IMAGE);
  const [newsletterData, setNewsLetterData] = useState<any>([]);
  const [errMsg, setErrMsg] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const config = newsletter.getConfig();
  const { HIGHMARK_API_URL, useCurrentRegionUbkHeader } = config;
  const newsletterEndPoint = `${HIGHMARK_API_URL}/digital/v3/members/benefits/newsletters`;
  const currentUbk: any = useCurrentRegionUbkHeader() || null;
  const ubkValue = currentUbk?.headers?.Brandubk || null;

  const renderContent = () => {
    if (newsletterData.length) {
      const latestYear: string = newsletterData[0]?.year;
      const latestSeason: any = newsletterData[0].season;
      return (
        <>
          <Card.Elevated
            imagePosition={{ '@initial': 'end', '@mobile': 'top' }}
            css={
              {
                boxSizing: 'border-box',
                width: '100%',
                maxWidth: '1132px',
                backgroundColor: '#fff',
                border: '1px solid #E0E3E6',
                margin: '56px auto 0',
                '@mobile': { margin: '$oneAndHalf auto 0' },
                '@mobileLandscape': { margin: '$oneAndHalf auto 0' },
              } as GDSStyleObject
            }
          >
            <Card.Image image={bannerImage as string} imageAlt="Latest Issue" />
            <Card.Section
              css={{
                padding: '$one',
              }}
            >
              <HeadingText
                size="lg"
                level="2"
                css={{ marginLeft: 0, flex: '0 0 100%', fontSize: '18px' }}
              >
                {`${latestSeason?.[0]?.name} ${latestYear} Issue`}
              </HeadingText>
              <ParagraphText
                css={{
                  marginTop: '$half',
                  marginBottom: '$two',
                  flex: '0 0 100%',
                  '@mobile': { marginBlockEnd: '24px !important' },
                  '@tablet': { marginBlockEnd: '24px !important' },
                  '@mobileLandscape': { marginBlockEnd: '32px !important' },
                  '@desktop': { marginBlockEnd: '55px !important' },
                  '@laptop': { marginBlockEnd: '55px !important' },
                  color: '#5A656D',
                }}
              >
                {CONSTANTS.VIEW_LATEST_ISSUE}
              </ParagraphText>
              <TextAction
                href={latestSeason[0].newsLetterUrl}
                css={{
                  fontSize: '$one',
                  lineHeight: 1,
                  alignSelf: 'end',
                  fontWeight: '600',
                  color: '#0074C9',
                  '&:focus': { outline: '$none !important' },
                  cursor: 'pointer',
                }}
                target="_blank"
                onClick={() =>
                  trackAnalyticsEvent(EVENT_NAME.RESOURCE_CLICKED, {
                    detail: CONSTANTS.READ_LATEST_ISSUE,
                    screen_name: CONSTANTS.HEALTH_MATTERS_NEWSLETTER,
                    product_area: CONSTANTS.HEALTH_MATTERS_NEWSLETTER,
                    document_type: 'health_matters_newsletter',
                    path: window.location.pathname,
                    event_author: CONSTANTS.ENGEN,
                  })
                }
                data-testid="readLatest"
              >
                {CONSTANTS.READ_LATEST_ISSUE}
              </TextAction>
            </Card.Section>
          </Card.Elevated>
          <Box
            css={{
              paddingTop: '$none',
              marginBottom: '$two',
              '@mobile': { paddingTop: '$half' },
              '@mobileLandscape': { paddingTop: '$half' },
              '@tablet': { paddingTop: '$half' },
            }}
          >
            {newsletterData.map((item: any, i: Number) => {
              const latestYearhasNoData = i === 0 && item.season.length < 2;
              return (
                <Box key={item.year} css={{ marginTop: '32px' }}>
                  {!latestYearhasNoData && (
                    <HeadingText
                      size="xl"
                      level="3"
                      css={
                        {
                          padding: '$none',
                          lineHeight: '33px',
                          marginTop: '$none',
                          fontSize: '22px',
                          '@mobile': {
                            marginBottom: '12px',
                            fontSize: '18px',
                            lineHeight: '26px',
                          },
                          '@tablet': {
                            marginBottom: '12px',
                            fontSize: '18px',
                            lineHeight: '26px',
                          },
                          '@mobileLandscape': {
                            marginBottom: '12px',
                            fontSize: '18px',
                            lineHeight: '26px',
                          },
                          '@laptop': { marginBottom: '$oneAndHalf' },
                          '@desktop': { marginBottom: '$oneAndHalf' },
                        } as GDSStyleObject
                      }
                    >
                      {`${item.year} Issues`}
                    </HeadingText>
                  )}

                  {item.season &&
                    item.season.map((value: any, j: Number) => {
                      const latestCard = i === 0 && j === 0;
                      if (!latestCard) {
                        return (
                          <TextAction
                            href={value?.newsLetterUrl}
                            target="_blank"
                            css={{
                              display: 'block',
                              cursor: 'pointer',
                              borderRadius: '6px',
                              '&:hover': {
                                textDecoration: 'none',
                                boxShadow: '0 0 0 2px #0066B1FF',
                              },
                              '&:focus': { outline: '0 !important' },
                            }}
                            data-testid="newsLetterButton"
                            key={`${value?.name}${item?.year}`}
                            onClick={() =>
                              trackAnalyticsEvent(EVENT_NAME.RESOURCE_CLICKED, {
                                detail: CONSTANTS.HEALTH_MATTERS_NEWSLETTER,
                                screen_name:
                                  CONSTANTS.HEALTH_MATTERS_NEWSLETTER,
                                product_area:
                                  CONSTANTS.HEALTH_MATTERS_NEWSLETTER,
                                document_type: 'health_matters_newsletter',
                                path: window.location.pathname,
                                event_author: CONSTANTS.ENGEN,
                              })
                            }
                          >
                            <Card.Elevated
                              css={{
                                border: 0,
                                alignItems: 'center',
                                paddingRight: '15px',
                                marginBottom: '$oneAndHalf',
                                '@mobile': { marginBottom: '12px' },
                                '@tablet': { marginBottom: '12px' },
                                '@mobileLandscape': {
                                  marginBottom: '12px',
                                },
                                '@laptop': { marginBottom: '$oneAndHalf' },
                                '@desktop': { marginBottom: '$oneAndHalf' },
                                backgroundColor: '#fff',
                                borderRadius: '6px',
                              }}
                              imagePosition={{ '@initial': 'end' }}
                            >
                              <Card.Image
                                image={rightChevGray as string}
                                imageAlt="Arrow"
                                css={{
                                  height: '12px',
                                  width: '6px',
                                }}
                              />
                              <Card.Section
                                padding={{
                                  '@initial': '$one',
                                  '@mobile': '$one',
                                  '@tablet': '$two',
                                  '@desktop': '$one',
                                }}
                              >
                                <StatusBadge
                                  showLight={false}
                                  label={`${value.name} ${item.year}`}
                                  status="brand"
                                  css={{
                                    borderRadius: '6px',
                                    backgroundColor: '#EAF5FD',
                                    padding: '$quarter 0',
                                    marginBottom: '$half',
                                    '& .GDS-utility-text': {
                                      fontSize: '12px',
                                      textTransform: 'uppercase',
                                      fontFamily: `Roboto, BlinkMacSystemFont, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif`,
                                      fontWeight: 700,
                                      letterSpacing: '0.6px',
                                      lineHeight: '16px',
                                      color: '#2C3135',
                                      '@mobile': { letterSpacing: 0 },
                                      '@tablet': { letterSpacing: 0 },
                                      '@mobileLandscape': {
                                        letterSpacing: '$none',
                                      },
                                    },
                                  }}
                                />
                                <HeadingText
                                  size="sm"
                                  level="4"
                                  css={{
                                    fontSize: '15px',
                                    lineHeight: '24px',
                                    fontFamily: `Poppins, BlinkMacSystemFont, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`,
                                    fontWeight: 600,
                                  }}
                                >
                                  {CONSTANTS.HEALTH_MATTERS_NEWSLETTER}
                                </HeadingText>
                                <ParagraphText
                                  css={{
                                    fontSize: '14px',
                                    margin: '5px 0',
                                    fontFamily: `Roboto, BlinkMacSystemFont, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif`,
                                    color: '#5A656D',
                                    lineHeight: '20px',
                                    fontWeight: 400,
                                  }}
                                >
                                  {`${CONSTANTS.UPLOADED} ${formatDate(
                                    value.uploadDate,
                                  )}`}
                                </ParagraphText>
                              </Card.Section>
                            </Card.Elevated>
                          </TextAction>
                        );
                      }
                      return null;
                    })}
                </Box>
              );
            })}
          </Box>
        </>
      );
    }
    return null;
  };

  const getNewsletterData = () => {
    getAccessToken(newsletterEndPoint, ubkValue).then((result: any) => {
      if (result && result.length) {
        setNewsLetterData(result);
        setIsLoading(false);
      }
      if (result.errMsg || result?.ErrorMessage || result?.errorMessage) {
        setIsLoading(false);
        setErrMsg(true);
      }
    });
  };

  const closeErrorModal = () => {
    setErrMsg(false);
    navigate(-1);
  };

  useEffect(() => {
    getNewsletterData();
    trackAnalyticsEvent(EVENT_NAME.SCREEN_LOADED, {
      screen_name: CONSTANTS.HEALTH_MATTERS_NEWSLETTER,
      product_area: CONSTANTS.HEALTH_MATTERS_NEWSLETTER,
      path: window.location.pathname,
      event_author: CONSTANTS.ENGEN,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      css={
        {
          display: 'flex',
          flexDirection: 'column',
        } as GDSStyleObject
      }
    >
      <Card.Flat
        css={
          {
            backgroundColor: '#EAF5FD',
            height: 'auto',
            '@desktop': { height: '152px' },
            '@laptop': { height: '152px' },
          } as GDSStyleObject
        }
      >
        <Card.Section
          css={
            {
              boxSizing: 'border-box',
              margin: '$none auto',
              width: '100%',
              maxWidth: '1132px',
              '@mobile': { padding: '10px 25px' },
              '@tablet': { paddingLeft: '25px' },
              '@mobileLandscape': { paddingLeft: '25px' },
              '@laptop': { padding: '$none 79px' },
              '@desktop': { padding: '$none 79px' },
            } as GDSStyleObject
          }
        >
          <Box
            css={
              {
                display: 'flex',
                marginTop: '15px',
                marginBottom: '15px',
                flexDirection: 'row',
                '@mobile': { marginBottom: '15px' },
              } as GDSStyleObject
            }
          >
            <TextAction onClick={() => navigate(-1)}>
              <img
                src={leftArrow as string}
                alt="Back"
                style={{
                  cursor: 'pointer',
                }}
              />
            </TextAction>
            <Box
              css={{
                display: 'inline-flex',
                height: '110px',
                paddingLeft: '22px',
                '@mobile': { height: 'auto' },
                '@mobileLandscape': { height: 'auto' },
                '@tablet': { height: 'auto' },
              }}
            >
              <HeadingText
                level="1"
                size={{
                  '@initial': 'xxl',
                  '@laptop': 'xxl',
                  '@desktop': 'xxl',
                  '@mobile': 'xxl',
                  '@mobileLandscape': 'xxl',
                  '@tablet': 'xxl',
                }}
                css={{
                  position: 'relative',
                  fontSize: '30px',
                  '@mobile': {
                    paddingLeft: '$none',
                    top: '0',
                    fontSize: '24px',
                    lineHeight: '35px',
                  },
                  '@tablet': { paddingLeft: '$none', top: '0' },
                  '@mobileLandscape': { paddingLeft: '$none', top: '0' },
                  '@laptop': { paddingLeft: '$none', top: '35px' },
                  '@desktop': { paddingLeft: '$none', top: '35px' },
                }}
              >
                {CONSTANTS.HEALTH_MATTERS_NEWSLETTER}
              </HeadingText>
            </Box>
          </Box>
        </Card.Section>
      </Card.Flat>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <StackLayout
          orientation="horizontal"
          spacing={{ '@desktop': '$two' }}
          data-testId="testLayoutId"
          css={{
            display: 'block',
            width: '100%',
            maxWidth: '880px',
            margin: '$none auto',
            '@desktop': { paddingBottom: '50px' },
            '@mobile': { padding: '$none $oneAndHalf 50px' },
            '@tablet': { padding: '$none $oneAndHalf 50px' },
            '@mobileLandscape': { paddingLeft: '25px' },
          }}
        >
          {renderContent()}
        </StackLayout>
      )}
      {errMsg ? <ErrorModal onClose={closeErrorModal} /> : null}
    </Box>
  );
}
